/**
 * Client Settings left rail (chunk3z-fix77): quality-score ring + section nav.
 *
 * Mirrors the reference layout: a circular quality ring at the top, an
 * (optional) "fill what's missing" affordance, and a vertical list of section
 * links that scroll to / highlight the matching section panel on the right.
 *
 * The active section is tracked by the parent via an IntersectionObserver and
 * passed down; clicking a link smooth-scrolls to that section's anchor id.
 */

import type { ScoreGap } from "./qualityScore";

export interface SettingsNavSection {
  id: string;
  label: string;
  /** Completion dot: 'full' (green), 'partial' (amber), 'empty' (grey). */
  state: "full" | "partial" | "empty";
}

export function QualityRing({
  score,
  color,
}: {
  score: number;
  color: string;
}) {
  // SVG ring: 0–100 maps to the stroke dash offset.
  const size = 120;
  const stroke = 8;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - Math.max(0, Math.min(100, score)) / 100);
  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="var(--border)"
            strokeWidth={stroke}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circ}
            strokeDashoffset={offset}
            style={{ transition: "stroke-dashoffset 400ms ease, stroke 300ms ease" }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-2xl font-semibold" style={{ color }}>
            {score}
          </span>
        </div>
      </div>
      <span className="text-text-muted text-xs mt-1">Quality Score</span>
    </div>
  );
}

export function SettingsSidebar({
  sections,
  activeId,
  onNavigate,
  score,
  scoreColor,
  gaps,
  onGapClick,
  onAiFillMissing,
  aiFillMissingBusy,
}: {
  sections: SettingsNavSection[];
  activeId: string | null;
  onNavigate: (id: string) => void;
  score: number;
  scoreColor: string;
  // fix269: highest-impact incomplete fields with their point values.
  gaps?: ScoreGap[];
  // Optional: jump to the section that owns a gap when its row is clicked.
  onGapClick?: (gapKey: string) => void;
  onAiFillMissing?: () => void;
  aiFillMissingBusy?: boolean;
}) {
  return (
    <aside className="w-60 shrink-0">
      <div className="sticky top-2 bg-bg-card border border-border rounded-lg shadow-card p-4">
        <QualityRing score={score} color={scoreColor} />

        {onAiFillMissing && (
          <div className="mt-4 pb-4 mb-1 border-b border-border-subtle">
            <button
              type="button"
              onClick={onAiFillMissing}
              disabled={aiFillMissingBusy}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-bg-card border border-border text-text-muted hover:border-accent hover:text-accent transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {aiFillMissingBusy ? "Filling…" : "✨ AI fill what's missing"}
            </button>
          </div>
        )}

        {/* fix269/fix275: "To reach 100%" — every remaining content gap, summing
            to the real score gap. */}
        {gaps && gaps.length > 0 && (
          <div className="mt-1 mb-1 pb-4 border-b border-border-subtle">
            <p className="text-xs font-semibold text-text-muted mb-2">
              To reach 100%
            </p>
            <ul className="space-y-1.5">
              {gaps.map((g) => {
                const row = (
                  <>
                    <span className="min-w-0 flex-1 truncate">
                      <span className="font-medium text-text">{g.label}</span>
                      <span className="text-text-subtle">
                        {" "}
                        — {g.hint}
                      </span>
                    </span>
                    <span className="shrink-0 font-semibold text-accent tabular-nums">
                      +{g.points}
                    </span>
                  </>
                );
                return (
                  <li key={g.key}>
                    {onGapClick ? (
                      <button
                        type="button"
                        onClick={() => onGapClick(g.key)}
                        className="w-full flex items-center gap-2 text-xs text-left text-text-muted hover:text-text transition-colors"
                        title={g.hint}
                      >
                        {row}
                      </button>
                    ) : (
                      <div
                        className="flex items-center gap-2 text-xs text-text-muted"
                        title={g.hint}
                      >
                        {row}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <nav className="mt-4 space-y-0.5">
          {sections.map((s) => {
            const active = s.id === activeId;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => onNavigate(s.id)}
                className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-sm text-left transition-colors ${
                  active
                    ? "bg-accent-subtle text-accent font-medium"
                    : "text-text-muted hover:text-text hover:bg-bg-hover/50"
                }`}
              >
                <span
                  className={`inline-block w-2 h-2 rounded-full shrink-0 ${
                    s.state === "full"
                      ? "bg-success"
                      : s.state === "partial"
                        ? "bg-warning"
                        : "bg-border-strong"
                  }`}
                />
                <span className="truncate">{s.label}</span>
              </button>
            );
          })}
        </nav>
      </div>
    </aside>
  );
}
